/* eslint-disable @typescript-eslint/no-shadow */
import React, { useState } from 'react';
import { Add } from '@sharefiledev/icons';
import { App, Button, Checkbox, Flex, Form, Modal, theme, Typography } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { PreparePageModalMaxHeight } from '../../../common/componentUtils';
import { Component } from '../../../common/componentUtils/types';
import { useAppStore } from '../../../common/store';
import { DocgenClient } from '../../../data/docgenClient';
import { PatchPayloadItem } from '../../../data/interfaces/APIRequest';
import { Role } from '../../../data/interfaces/DocumentTemplate';
import { InputValidation } from '../../../data/interfaces/form';
import { t } from '../../../utils';
import { isUniqueName, validateName, validateRoles } from '../../../utils/validator';
import RoleItem from './RoleItem';
import SortableContainer from './SortableContainer';

const { Text } = Typography;

const getEmptyRecipient = (sequence = 0): Role & { isCreatedNew: boolean } => ({
	id: uuidv4(),
	name: '',
	sequence,
	isSender: false,
	isCreatedNew: true,
});

const useRoles = (documentTemplate, updateDocumentTemplate) => {
	const roles: Role[] = documentTemplate?.roles;

	const setRoles = (roles: Role[]) => {
		updateDocumentTemplate({ roles });
	};

	return { roles, setRoles };
};

const useSignerSequencing = (documentTemplate, updateDocumentTemplate) => {
	const signerSequencing = !!documentTemplate?.signerSequencing;

	const setSignerSequencing = (sequence: boolean) => {
		updateDocumentTemplate({ signerSequencing: sequence });
	};

	return { signerSequencing, setSignerSequencing };
};

type RolesProps = {
	isShownInModal?: boolean;
	onSave?: (role: Role) => void;
};

export const Roles = ({ isShownInModal, onSave }: RolesProps) => {
	const { notification } = App.useApp();
	const { documentTemplate, updateDocumentTemplate } = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
		updateDocumentTemplate: state.updateDocumentTemplate,
	}));
	const [modal, contextHolder] = Modal.useModal();
	const { roles, setRoles } = useRoles(documentTemplate, updateDocumentTemplate);
	const { signerSequencing, setSignerSequencing } = useSignerSequencing(
		documentTemplate,
		updateDocumentTemplate
	);
	const [localRoles, setLocalRoles] = useState(documentTemplate?.roles);
	const [deletedRolesWithFields, setDeletedRolesWithFields] = useState<string[]>([]);
	const items = isShownInModal ? localRoles : roles;

	const toggleSignerSequence = () => {
		setSignerSequencing(!signerSequencing);
	};

	const updateRoles = roles => {
		if (isShownInModal) {
			setLocalRoles(roles);
		} else {
			setRoles(roles);
		}
	};

	const handleAddRole = () => {
		if (items?.length) {
			const sequence = items.length;
			updateRoles([...items, getEmptyRecipient(sequence)]);
		} else {
			updateRoles([getEmptyRecipient()]);
		}
	};

	const updateRoleNameInStore = (updatedName, roleId) => {
		updateRoles(
			items.map(role =>
				role.id === roleId ? { ...role, name: updatedName, isCreatedNew: false } : role
			)
		);
	};

	function isComponentPresent(id: string) {
		return documentTemplate?.fields?.find(
			(component: Component) => component?.assignedTo?.id === id
		);
	}

	const deleteRoleWithFieldModalConfig = {
		title: t('fieldsAssignedWarningHeader'),
		content: <Text>{t('fieldsAssignedWarningMessage')}</Text>,
		okText: t('confirm'),
	};

	const handleWarningForRoleRemoval = async confirmDeleteCall => {
		const confirmed = await modal.confirm(deleteRoleWithFieldModalConfig);
		if (confirmed) {
			confirmDeleteCall();
		}
	};

	function getUpdatedRolesAndDeletedRole(roleId: string) {
		let roleToDelete = null;

		const filteredRoles = items.filter(r => {
			if (r.id !== roleId) {
				return true;
			} else {
				roleToDelete = r;
				return false;
			}
		});

		const updatedRoles = filteredRoles.map((r, index) => ({
			...r,
			sequence: index,
		}));

		return { updatedRoles, roleToDelete };
	}

	function deleteRole(roleId: string) {
		const { updatedRoles } = getUpdatedRolesAndDeletedRole(roleId);
		updateRoles(updatedRoles);
	}

	const handleDeleteRole = (roleId: string) => {
		if (isComponentPresent(roleId)) {
			handleWarningForRoleRemoval(() => {
				deleteRole(roleId);
				if (isShownInModal) {
					setDeletedRolesWithFields(prev => [...prev, roleId]);
				} else {
					const updatedComponents = documentTemplate?.fields?.filter(
						(component: Component) => component?.assignedTo?.id !== roleId
					);
					updateDocumentTemplate({ fields: updatedComponents });
				}
			});
			return;
		}
		deleteRole(roleId);
	};

	function updateRoleSequence(currentIndex, newIndex) {
		const clonedRoles = [...items];
		const [roleToMove] = clonedRoles.splice(currentIndex, 1);
		clonedRoles.splice(newIndex, 0, roleToMove);

		const rolesWithUpdatedSequence = clonedRoles.map((role, index) => ({
			...role,
			sequence: index,
		}));

		updateRoles(rolesWithUpdatedSequence);
	}

	const handleSaveRoles = () => {
		const roleErrorMessage = validateRoles(items);

		if (roleErrorMessage !== '') {
			notification.error({
				message: roleErrorMessage,
			});
		} else {
			setRoles(items);

			if (items) {
				onSave?.(items?.[0]);
				saveData(items);
			}
		}
	};

	const saveData = async (items: Role[]) => {
		const payload: PatchPayloadItem[] = [
			{
				op: 'replace',
				path: '/roles',
				value: items,
			},
			{
				op: 'replace',
				path: '/signerSequencing',
				value: documentTemplate.signerSequencing,
			},
		];

		if (documentTemplate?.fields) {
			let updatedFields = documentTemplate.fields;

			if (deletedRolesWithFields.length > 0) {
				updatedFields = documentTemplate.fields.filter(
					(component: Component) =>
						!deletedRolesWithFields.includes(component?.assignedTo?.id)
				);
			}

			payload.push({
				op: 'replace',
				path: '/fields',
				value: updatedFields,
			});
		}

		const patchResponse = await DocgenClient.updateDocumentTemplate(
			payload,
			documentTemplate.rid
		);

		updateDocumentTemplate(patchResponse);

		setDeletedRolesWithFields([]);
	};

	const { token } = theme.useToken();

	const modalStyles = isShownInModal
		? {
				maxHeight: PreparePageModalMaxHeight,
				overflow: 'auto',
				marginBlockEnd: token.marginLG,
				padding: token.paddingXXS,
		  }
		: {};

	const validateRole = (role: Role) => {
		const isNewlyAddedVariable = (role as any).isCreatedNew;

		let result: InputValidation = {
			status: 'success',
			message: '',
		};
		if (isNewlyAddedVariable) {
			return result;
		}

		result = validateName(role.name, t('roleNameValidationError'));
		if (result.status === 'success') {
			if (!isUniqueName(role.name, items)) {
				result = {
					status: 'error',
					message: t('roleNameUniqueError'),
				};
			}
		}

		return result;
	};

	return (
		<Flex vertical>
			{items && items.length > 1 && (
				<Flex style={{ marginBlockEnd: token.marginMD }}>
					<Checkbox onChange={toggleSignerSequence} checked={signerSequencing}>
						{t('startPage.setSignerSequence')}
					</Checkbox>
				</Flex>
			)}

			{items && items.length > 0 && (
				<Flex vertical style={modalStyles}>
					<Form layout="vertical">
						<SortableContainer items={items} handleItemDragEnd={updateRoleSequence}>
							{items
								.sort((a, b) => a.sequence - b.sequence)
								.map((role, index) => (
									<RoleItem
										id={index + 1}
										key={role.id}
										role={role}
										onRemove={handleDeleteRole}
										onUpdate={updateRoleNameInStore}
										signerSequencing={signerSequencing}
										onSequenceInputChange={updateRoleSequence}
										rolesLength={items.length}
										isShownInModal={isShownInModal}
										validationState={validateRole(role)}
									/>
								))}
						</SortableContainer>
						{contextHolder}
					</Form>
				</Flex>
			)}

			<Flex justify="space-between">
				<Button
					type="primary"
					icon={<Add size={16} />}
					onClick={() => handleAddRole()}
					disabled={!documentTemplate?.file?.fileName}
				>
					{items?.length ? t('startPage.addAnotherRole') : t('startPage.addNewRole')}
				</Button>
				{isShownInModal && <Button onClick={() => handleSaveRoles()}>{t('save')}</Button>}
			</Flex>
		</Flex>
	);
};
